import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    ViewChild,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { BaseForm } from '../../../../base-form';
import { TareasBioFormDefinition } from './form-control/form-definition';
import moment from 'moment';
import { renderHtml2Canvas } from 'src/app/common/html2-canvas-renderer';

import type { TareasBioModel } from '../../../../../models/tareas/tareas-bio.model';
import type { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import {
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
    DropdownData,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareasBioEnvironmentDefinition } from '../environment-variables';
import { createJsPdf } from 'src/app/common/pdf-document-renderer';

@Component({
    selector: 'app-tareas-bio-pulverizacion',
    standalone: false,
    templateUrl: './tareas_bio-form.component.html',
    styleUrls: ['./tareas_bio-form.component.scss'],
})
export class TareasBioFormComponent extends BaseForm<TareasBioModel> implements OnInit, OnDestroy {
    @ViewChild('imagenCapturada') imagenCapturada: ElementRef = new ElementRef('');

    public env: TareasBioEnvironmentDefinition = new TareasBioEnvironmentDefinition();
    public form!: TareasBioFormDefinition;
    public override model: TareasBioModel = {};
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public loadingData = true;
    public initialPreviousData: TareasBioModel | null = null;
    public reactiveFormRecordId = '';
    public showRecetaTareaBio = false;
    public isGeneratingCanvas = false;
    public receta: TareasBioModel = {};


    public parcela: ParcelasModel = {
        id: '',
        nombre: '',
        superficie: '',
        finca: '',
        sector: ''
    };

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public tareasApi: TareasApiService,
        public trabajadores: TrabajadoresApiService,
        public override router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.bio.PUT,
            tareasApi.bio.POST,
            'bio',
            'Actualizar Tarea Bio',
            'Realizar Tarea Bio',
            'Duplicar Tarea Bio',
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            if (this.route.snapshot.paramMap.get('deCalendario')?.toString() === 'true') {
                this.pathToGoBack = 'tratamientos-control';
            }

            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.softInit(this.getType());
            const routeModel = this.createRouteSnapshotModel();

            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            const initialModel = this.initializeHostModel(this.toTareasBioModel(routeModel));

            this.initialPreviousData = this.resolveInitialPreviousData(initialModel);
            this.form = new TareasBioFormDefinition(this.getType(), initialModel);
            this.form.model = this.toTareasBioModel(initialModel);
            this.configureDeclarativeCatalogDataSources();

            await this.initFormData();
            this.syncModelFromForm();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario bio.', error);
        }
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    private createRouteSnapshotModel(): Partial<TareasBioModel> {
        const snapshotParams = this.route.snapshot.params as Record<string, unknown>;
        const routeModel: Record<string, TareasBioModel[keyof TareasBioModel]> = {};

        for (const [key, value] of Object.entries(snapshotParams)) {
            if (value !== undefined) {
                routeModel[key] = value as TareasBioModel[keyof TareasBioModel];
            }
        }

        return routeModel as Partial<TareasBioModel>;
    }

    private resolveInitialPreviousData(initialModel: TareasBioModel): TareasBioModel | null {
        if (this.getType() === FormType.DUPLICATION) {
            return this.toTareasBioModel(initialModel);
        }

        return null;
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TareasBioModel>): void {
        const stableValue = this.captureStableValueChange(event.value as TareasBioModel);

        this.form.model = this.toTareasBioModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TareasBioModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as TareasBioModel);

        this.form.model = this.toTareasBioModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.updateHorasTotales();
    }

    public generarReceta(): void {
        this.receta = {
            ...this.model,
            finca_nombre: this.resolveSelectedDropdownEntity(
                this.form.lists.fincas,
                this.form.model.id_finca ?? this.model.id_finca,
            )?.nombre
                ?? this.model.finca_nombre
                ?? '',
            dosis1: this.concatDoseAndUnit(this.model.dosis1, this.model.ud_real_1),
            dosis2: this.concatDoseAndUnit(this.model.dosis2, this.model.ud_real_2),
            dosis3: this.concatDoseAndUnit(this.model.dosis3, this.model.ud_real_3),
            fecha: this.model.fecha ? moment(this.model.fecha).format('LL') : '',
        };

        this.showRecetaTareaBio = true;
        const elements = document.getElementsByClassName('p-dialog-content');

        setTimeout(() => {
            if (elements[0]) {
                elements[0].scrollTop = 0;
            }
        }, 100);
    }

    public imprimirReceta(): void {
        this.isGeneratingCanvas = true;
        const config = {
            canvasScale: 2,
            width: 220,
        };
        const canvasElement = document.getElementById('html2canvasDiv');

        renderHtml2Canvas(canvasElement ?? new HTMLElement(), { scale: config.canvasScale, logging: true }).then(async canvas => {
            this.isGeneratingCanvas = false;

            const pdf = await createJsPdf('l', 'mm', 'a4');
            const data = canvas.toDataURL('image/png');

            pdf.addImage(data, 'png', 10, 10, config.width, canvas.height / (canvas.width / config.width), 'alias', 'FAST');
            pdf.save('Bio.pdf');

            return;
        }).catch((error: unknown) => {
            this.isGeneratingCanvas = false;
            console.log('catch en imprimirReceta: ' + error);
        });
    }

    private initFormData(): Promise<boolean> {
        this.loadingData = true;

        return Promise.resolve(true);
    }

    private configureDeclarativeCatalogDataSources(): void {
        this.form.setCatalogDataSources({
            clientes: this.clientesApi.clientesGET,
            fincas: this.fincasApi.fincasGET,
            sectores: this.fincasApi.sectoresGET,
            parcelas: this.fincasApi.parcelasGET,
            trabajadores: this.trabajadores.trabajadoresGET,
            maquinarias: this.trabajadores.maquinariaGET,
        });
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<TareasBioModel>,
        lastChangedFieldName: keyof TareasBioModel | string | null | undefined,
    ): void {
        const changedFieldName = String(lastChangedFieldName ?? '');

        this.form.model = this.toTareasBioModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (changedFieldName === 'id_cliente') {
            this.onClientChanged();
        }
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        this.clearLocationChildren(['id_finca', 'id_sector', 'ids_parcelas']);

        if (!selectedClientId || this.env.appName === 'agricar') {
            return;
        }

        this.reloadClientDropdowns();
    }

    private reloadClientDropdowns(): void {
        this.loadingData = true;
        this.formBuilderRef?.refreshDeclarativeDropdown('id_finca');
        this.formBuilderRef?.refreshDeclarativeDropdown('id_sector');
        this.formBuilderRef?.refreshDeclarativeDropdown('ids_parcelas');
        this.loadingData = false;
        this.cdr.markForCheck();
    }

    private clearLocationChildren(fieldNames: string[]): void {
        const patch = fieldNames.reduce((result, fieldName) => ({ ...result, [fieldName]: null }), {} as Record<string, null>);

        this.form.model = this.toTareasBioModel({
            ...this.form.model,
            ...patch,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm(patch, true);
    }

    private markReactiveFormReady(): void {
        this.loadingData = false;
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.cdr.markForCheck();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toTareasBioModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toTareasBioModel(sourceModel: Partial<TareasBioModel> | ReactiveFormValue<TareasBioModel>): TareasBioModel {
        const normalizedModel: Record<string, unknown> = {
            ...(sourceModel as unknown as Record<string, unknown>),
        };

        return normalizedModel as unknown as TareasBioModel;
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim().length > 0) {
            return error.message;
        }

        if (typeof error === 'string' && error.trim().length > 0) {
            return error;
        }

        return 'No se ha podido preparar el formulario bio.';
    }

    private updateHorasTotales(): void {
        const horasTotales = this.form.calculateHorasTotales();

        if (this.form.model.horas_totales === horasTotales) {
            return;
        }

        this.form.model = this.toTareasBioModel({
            ...this.form.model,
            horas_totales: horasTotales,
        });
        this.syncModelFromForm();

        this.formBuilderRef?.updateForm({ horas_totales: horasTotales }, true);
    }

    private concatDoseAndUnit(dose: unknown, unit: unknown): string {
        return [dose, unit]
            .map((value) => String(value ?? '').trim())
            .filter((value) => value.length > 0)
            .join(' ');
    }

    private resolveSelectedDropdownEntity<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity | null {
        const tokens = this.getReactiveValueTokens(selectedValue);

        if (tokens.length === 0) {
            return null;
        }

        return dropdownData.values
            .map((option) => this.getDropdownOptionEntity<TEntity>(option))
            .find((entity): entity is TEntity => entity !== null && tokens.includes(this.getReactiveValueId(entity)))
            ?? null;
    }

    private getReactiveValueTokens(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getReactiveValueTokens(item));
        }

        if (typeof value === 'object' && value !== null) {
            const entityId = this.getReactiveValueId(value);

            return entityId.length > 0 ? [entityId] : [];
        }

        return String(value ?? '')
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }

    private getDropdownOptionEntity<TEntity extends object>(
        option: { readonly value: TEntity | null; readonly raw?: unknown },
    ): TEntity | null {
        if (this.isRecord(option.raw)) {
            return option.raw as unknown as TEntity;
        }

        if (this.isRecord(option.value)) {
            return option.value as unknown as TEntity;
        }

        return null;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
